/**
 * Pricing configuration: credit packages, the free-amount top-up, auto
 * top-up defaults, feature access, plan caps — and the retired Pro
 * subscription, kept only for its grandfathered subscribers.
 *
 * Since 2026-10 FirmenData is credit-only: every signed-in account gets every
 * feature (the former Pro set), and credits are bought as packages or as a
 * free-amount top-up. Pro is no longer sold — checkout rejects new
 * subscriptions and signup starts no trial — but existing Pro subscriptions
 * keep renewing and keep their 5,000 credits per period until cancelled.
 *
 * Pro product ID — read from env so the same code works against Stripe Test
 * Mode locally (`sk_test_…` + the test product ID) and Live Mode in prod
 * (`sk_live_…` + the live product ID). Stripe Test and Live are completely
 * separate object spaces, so the IDs always differ — there's no single
 * hardcodable constant that covers both modes. Still needed for the
 * grandfathered subscriptions' period grants and role sync.
 */
const PRO_PRODUCT_ID = process.env.NEXT_PUBLIC_STRIPE_PRO_PRODUCT_ID;

export const STRIPE_PRODUCT_TO_ROLE: Record<string, string> = PRO_PRODUCT_ID
  ? { [PRO_PRODUCT_ID]: 'Pro' }
  : {};

/**
 * Monthly Pro credit allowance. The webhook resets any leftover balance and
 * grants this amount on every `invoice.payment_succeeded` (first payment +
 * each renewal). For yearly plans it covers month zero; the credit-refill
 * cron resets and grants months 1..11 at clamped monthly UTC anchors within
 * that Stripe billing period.
 */
export const PRO_CREDITS_PER_PERIOD = 5000;

/**
 * Free credits every account gets each calendar month (Europe/Berlin). The
 * backend resets the free bucket to this on the 1st (leftovers expire, no
 * stacking), spends it before purchased credits and grants it at signup.
 * Purchased credits never expire.
 */
export const FREE_MONTHLY_CREDITS = 800;

/**
 * Length of the retired signup Pro trial. No new trial starts (signup no
 * longer creates one); the value only labels trials that are still running
 * out in old notifications.
 */
export const PRO_TRIAL_DAYS = 7;

/**
 * Per-tier caps for saved searches.
 *
 * Enterprise is capped rather than uncapped because every card on the manage
 * page costs a COUNT over `mv_search` to refresh — 50 is a real step up from
 * the standard 10 while keeping that page's worst case bounded. Enforced in
 * the /api/saved-searches routes via lib/plan-limits.ts savedSearchLimits();
 * the backend keeps a plan-blind 100 ceiling. Hitting the cap offers
 * "replace", so the feature never becomes a dead end.
 */
export const STANDARD_SAVED_SEARCHES_LIMIT = 10;
export const ENTERPRISE_SAVED_SEARCHES_LIMIT = 50;

/**
 * Stripe Product ID → credits granted per billing period, for the
 * grandfathered Pro subscriptions. Products absent here fall back to
 * {@link PRO_CREDITS_PER_PERIOD} in the webhook, so a missing/wrong env var
 * still grants the Pro allowance — better safe than silently giving paying
 * users 0 credits.
 */
export const STRIPE_PRODUCT_TO_CREDITS: Record<string, number> = PRO_PRODUCT_ID
  ? { [PRO_PRODUCT_ID]: PRO_CREDITS_PER_PERIOD }
  : {};

/**
 * Credit top-up: one checkout unit = 1 € net = 100 credits. Users pick the
 * quantity directly in Stripe Checkout (adjustable quantity); the webhook
 * grants `quantity × CREDIT_TOPUP_CREDITS_PER_UNIT` into the durable
 * purchased wallet (survives Pro period resets — see the backend ledger).
 * This is the list price every package discount is measured against: 1 ct
 * per credit.
 */
export const CREDIT_TOPUP_CREDITS_PER_UNIT = 100;
/** Net price per top-up unit in euro cents (1 €). VAT is added at checkout. */
export const CREDIT_TOPUP_UNIT_AMOUNT_CENTS = 100;
/** Default quantity preselected in Stripe Checkout. */
export const CREDIT_TOPUP_DEFAULT_QUANTITY = 5;
/**
 * Quantity bounds enforced by Stripe Checkout's quantity picker and clamped
 * server-side in create-checkout-session: a top-up is at least 5 € (500
 * credits) — smaller one-off card payments cost more in fees than they bring.
 */
export const CREDIT_TOPUP_MIN_QUANTITY = 5;
export const CREDIT_TOPUP_MAX_QUANTITY = 500;
/**
 * Stable Stripe price lookup_key used to find (or lazily create) the top-up
 * price in whichever mode (test/live) the server runs in — same pattern as
 * the VAT tax rate, no dashboard setup or per-mode env var required.
 */
export const CREDIT_TOPUP_PRICE_LOOKUP_KEY = 'credit_topup_100_per_eur';

/**
 * Credit packages — fixed bundles at a volume discount against the top-up
 * list price (1 € = 100 credits). Prices are NET in euro cents; 19 % VAT is
 * added at checkout like every other price. Bought credits land in the
 * durable purchased wallet and never expire.
 *
 * Each package's Stripe price is found (or lazily created) by `lookupKey`,
 * like the top-up price, so test mode works with no dashboard setup. The key
 * carries credits and price: changing either here makes a NEW key, hence a
 * new Stripe price — an existing price's amount can never be edited in
 * Stripe, so reusing the key would keep charging the old amount.
 */
export type CreditPackageId = 'starter' | 'standard' | 'business' | 'professional';

export interface CreditPackage {
  id: CreditPackageId;
  credits: number;
  /** Net price in euro cents. */
  priceCents: number;
  lookupKey: string;
}

const creditPackage = (id: CreditPackageId, credits: number, priceCents: number): CreditPackage => ({
  id,
  credits,
  priceCents,
  lookupKey: `credit_package_${id}_${credits}_${priceCents}`,
});

export const CREDIT_PACKAGES: readonly CreditPackage[] = [
  creditPackage('starter', 1_000, 1_000),
  creditPackage('standard', 5_000, 4_000),
  creditPackage('business', 20_000, 14_000),
  creditPackage('professional', 60_000, 36_000),
];

/**
 * The packages the checkout modal offers as cards (next to "Other", the
 * free-amount top-up). The pricing page still sells every package; a
 * package left out here still shows in the modal when its pricing-page
 * button opened it.
 */
export const CHECKOUT_PACKAGE_IDS: readonly CreditPackageId[] = ['starter', 'standard', 'professional'];

/** The package an auto top-up buys unless the user picks another. */
export const AUTO_TOPUP_DEFAULT_PACKAGE_ID: CreditPackageId = 'standard';
/** Balance below which an auto top-up fires, unless the user picks another. */
export const AUTO_TOPUP_DEFAULT_THRESHOLD = 500;
/** Threshold bounds; it must also stay below the package's credits, so one
 *  top-up always lifts the balance back over it. */
export const AUTO_TOPUP_MIN_THRESHOLD = 100;
export const AUTO_TOPUP_MAX_THRESHOLD = 10_000;
/** Auto top-ups per calendar month (UTC), unless the user picks another. */
export const AUTO_TOPUP_DEFAULT_MONTHLY_CAP = 3;
export const AUTO_TOPUP_MAX_MONTHLY_CAP = 10;

/**
 * Bulk data delivery's entry price in euros per month, net. Display only:
 * bulk is sold by contract through the contact dialog, not through Stripe.
 */
export const BULK_DELIVERY_FROM_MONTHLY_EUR = 500;

/**
 * Who may use each feature that used to need Pro. `'signedIn'` = any account
 * (guests get the signup prompt); `'public'` = guests too. One line per
 * feature, so moving a feature behind sign-in or opening it to guests is a
 * one-line change here.
 */
export type FeatureAccess = 'public' | 'signedIn';

export const FEATURE_ACCESS = {
  lists: 'signedIn',
  aiSearch: 'signedIn',
  advancedSearchFilters: 'signedIn',
  excelExport: 'signedIn',
  tendersExport: 'signedIn',
  bilanzHtmlDownload: 'signedIn',
  batchEnrich: 'signedIn',
} as const satisfies Record<string, FeatureAccess>;

export type GatedFeature = keyof typeof FEATURE_ACCESS;
